import {
  faCalendarDays,
  faChevronDown,
  faComment,
  faCompass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components/macro";
import { useCoachConnect } from "./hooks/useCoachConnect";
import PanelSkeleton from "./modules/PanelSkeleton";
import { analytics, PANEL_EVENTS } from "./utils/analytics";
import NextStepsCard from "./variants/NextStepsCard";
import Od15Card from "./variants/Od15Card";
import PlatinumCoachingSessionCard from "./variants/PlatinumCoachingSessionCard";
import UpComingEventCard from "./variants/UpComingEventCard";

// ─── Layout ───────────────────────────────────────────────────────────────────

const CoachingColumn = styled.section`
  min-width: 150px;
  max-width: 300px;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakPoint.tablet}) {
    max-width: 100%;
    width: 100%;
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 70px;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakPoint.tablet}) {
    position: static;
    padding-bottom: 24px;
    gap: 12px;
  }
`;

const NavMenuCard = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.md};
`;

const NavMenuTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.heading};
  margin: 0 0 2px;
`;

const NavMenuSubtitle = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
  margin: 0 0 12px;
`;

const NavMenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NavMenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 10px;
  text-align: left;
  transition: background 0.15s ease;

  font-family: ${({ theme }) => theme.fontFamily.main};
  &:hover {
    background: ${({ theme }) => theme.colors.altBackground};
  }
`;

const NavMenuIcon = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  width: 16px;
  text-align: center;
  color: ${({ active, theme }) =>
    active ? theme.colors.heroBlue : theme.colors.body};
  transition: color 0.15s ease;
`;

const NavMenuMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const NavMenuItemTitle = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ active, theme }) =>
    active ? theme.colors.heading : "hsla(0, 0%, 24%, 0.8)"};
  transition: color 0.15s ease;
`;

const NavMenuItemDesc = styled.span`
  display: block;
  font-size: 10px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.body};
  margin-top: 2px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NAV_ITEMS = [
  {
    key: "coaching",
    icon: faComment,
    title: "Coaching",
    desc: "One-to-one, confidential sessions to support your progress",
  },
  {
    key: "events",
    icon: faCalendarDays,
    title: "Events",
    desc: "Masterclasses to deepen your thinking and build capability",
  },
  {
    key: "vision",
    icon: faCompass,
    title: "Vision",
    desc: "Turn reflection into clear direction and action",
  },
];

/**
 * GPDS-893 — Collapsible toggle button, visible on tablet/mobile only.
 * Desktop: always open (toggle hidden via CSS).
 */
/* const CollapseToggle = styled.button`
  display: none;
  width: 100%;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 0.75em;
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.heading};
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakPoint.tablet}) {
    display: flex;
  }
`; */

/* Prevents the custom `isOpen` prop from being forwarded to the DOM element */
const CollapsibleContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakPoint.tablet}) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    gap: 12px;
  }
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 1em;
  box-shadow: ${({ theme }) => theme.boxShadow.md};
  padding: ${({ theme }) => theme.spacing.md};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.body};
  text-align: center;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.altBackground};
  border-radius: 0.65em;
  padding: 4px;
`;

const tabPop = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

const TabBtn = styled.button`
  flex: 1;
  border: none;
  border-radius: 0.5em;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fontFamily.main};
  cursor: pointer;
  transition: background 0.25s ease, color 0.2s ease, box-shadow 0.25s ease;
  background: ${({ active, theme }) =>
    active ? theme.colors.primaryBackground : "transparent"};
  color: ${({ active, theme }) =>
    active ? theme.colors.heading : theme.colors.body};
  box-shadow: ${({ active, theme }) =>
    active ? theme.boxShadow.accountCard : "none"};
  animation: ${({ active }) =>
    active
      ? css`
          ${tabPop} 0.25s ease
        `
      : "none"};
  &:hover {
    color: ${({ theme }) => theme.colors.heading};
  }
`;

const EligibilityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 2px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.heroBlue};
`;

const TAB_LABELS = {
  coaching: "Dedicated Support",
  events: "Learn and Grow",
  vision: "Define Your Direction",
};

const ConfidentialityCard = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.sm};
  box-shadow: ${({ theme }) => theme.boxShadow.accountCard};
  margin-top: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fontFamily.main};
`;

const ConfidentialityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ConfidentialityTitle = styled.span`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.heading};
`;

const ConfidentialityChevron = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
  transition: transform 0.2s ease;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0deg)")};
  display: inline-flex;
`;

const ConfidentialityBody = styled.p`
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
  line-height: 1.55;
`;

function ConfidentialityNote() {
  const [open, setOpen] = useState(false);
  return (
    <ConfidentialityCard onClick={() => setOpen((prev) => !prev)}>
      <ConfidentialityHeader>
        <ConfidentialityTitle>Confidentiality</ConfidentialityTitle>
        <ConfidentialityChevron open={open}>
          <FontAwesomeIcon icon={faChevronDown} />
        </ConfidentialityChevron>
      </ConfidentialityHeader>
      {open && (
        <ConfidentialityBody>
          While we may use the data in aggregated and de-identified form, no
          personal information will be shared with your organisation or any
          external party without your explicit consent. For more details, you
          may review our privacy statement.
        </ConfidentialityBody>
      )}
    </ConfidentialityCard>
  );
}

// ─── Tier routing ─────────────────────────────────────────────────────────────

function TierContent({ data = {}, onBookingAction, activeTab }) {
  const errCard = (
    <ErrorCard key="errCard">
      Unable to determine your coaching tier. Please refresh or contact support.
    </ErrorCard>
  );

  const coachingSessionsCard = (
    <PlatinumCoachingSessionCard
      key={"coachingSessionCard"}
      data={data?.tier?.dedicated_coach}
      onBookingAction={onBookingAction}
    />
  );
  const od15Card = (
    <Od15Card
      key={"od15Card"}
      data={data?.tier?.od15}
      onBookingAction={onBookingAction}
      od15BookingUrl={data?.od15BookingUrl}
    />
  );

  const nextStepCard = <NextStepsCard key={"nextStepCard"} />;
  const upcomingEventCard = <UpComingEventCard key={"upcomingEventCard"} />;

  if (activeTab === "events") return [upcomingEventCard];
  if (activeTab === "vision") return [nextStepCard];

  // coaching tab (default)
  const cards = [];
  if (data?.tier?.dedicated_coach?.sessions?.length > 0) {
    cards.push(coachingSessionsCard);
  } else {
    cards.push(od15Card);
  }

  if (cards.length === 0) {
    cards.push(errCard);
  }
  return cards;
}

// ─── Booking action handler ───────────────────────────────────────────────────

/**
 * Handles booking CTA actions. Opens the booking URL in a new tab.
 * TODO: wire to calendar/booking modal once booking URLs are confirmed.
 */
function useBookingAction(data) {
  return function handleBookingAction(sessionId, actionType) {
    console.log("Booking action triggered:", { sessionId, actionType }); // Debug log to confirm function is called with correct parameters
  };
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

/**
 * GPDS-887 — Panel placement and layout shell
 * - Sticky sidebar on desktop
 * - Collapsible (collapsed by default) on tablet/mobile
 * - Loading skeleton while tier data fetches
 * - Tier routing via TierContent
 */
export default function Panel() {
  const [activeTab, setActiveTab] = useState("coaching");

  const isOpen = true; // Always open for now, per design feedback and pending analytics

  const { status, data } = useCoachConnect();

  const onBookingAction = useBookingAction(data);

  const isLoading = status === "idle" || status === "fetching";
  /* const isError = status === "error"; */

  useEffect(() => {
    analytics.track(PANEL_EVENTS.PANEL_VIEWED);
  }, []);

  function handleTabChange(tab) {
    setActiveTab(tab);
    analytics.track(PANEL_EVENTS.TAB_SWITCHED, { tab });
  }

  return (
    <CoachingColumn>
      <StickyContainer>
        <NavMenuCard>
          <NavMenuTitle>My CoachConnect</NavMenuTitle>
          <NavMenuSubtitle>
            Everything you need for your coaching journey.
          </NavMenuSubtitle>
          <NavMenuList>
            {NAV_ITEMS.map((item) => (
              <NavMenuItem
                onClick={() => handleTabChange(item.key)}
                key={item.key}
                active={activeTab === item.key}
              >
                <NavMenuIcon>
                  <FontAwesomeIcon icon={item.icon} />
                </NavMenuIcon>
                <NavMenuMeta>
                  <NavMenuItemTitle a>{item.title}</NavMenuItemTitle>
                  <NavMenuItemDesc>{item.desc}</NavMenuItemDesc>
                </NavMenuMeta>
                {/* <NavMenuChevron>
                  <FontAwesomeIcon icon={faChevronRight} />
                </NavMenuChevron> */}
              </NavMenuItem>
            ))}
          </NavMenuList>
        </NavMenuCard>

        <TabBar>
          <TabBtn
            active={activeTab === "coaching"}
            onClick={() => handleTabChange("coaching")}
          >
            Coaching
          </TabBtn>
          <TabBtn
            active={activeTab === "events"}
            onClick={() => handleTabChange("events")}
          >
            Events
          </TabBtn>
          <TabBtn
            active={activeTab === "vision"}
            onClick={() => handleTabChange("vision")}
          >
            Vision
          </TabBtn>
        </TabBar>

        <EligibilityBadge>{TAB_LABELS[activeTab]}</EligibilityBadge>

        {/* GPDS-893 — Mobile/tablet collapse toggle */}
        {/* <CollapseToggle
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-controls="coach-connect-panel-content"
        >
          <span>{isOpen ? "Hide panel" : "Show coaching panel"}</span>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </CollapseToggle> */}

        <CollapsibleContent
          id="coach-connect-panel-content"
          isOpen={isOpen}
          aria-hidden={!isOpen}
        >
          {isLoading ? (
            <PanelSkeleton />
          ) : (
            <TierContent
              data={data}
              onBookingAction={onBookingAction}
              activeTab={activeTab}
            />
          )}

          {/*  {isError && (
            <ErrorCard key="errCard">
              Unable to load your coaching panel. Please try again later.
            </ErrorCard>
          )} */}

          <ConfidentialityNote />
        </CollapsibleContent>
      </StickyContainer>
    </CoachingColumn>
  );
}
